import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Card, CardHeader } from "@ui5/webcomponents-react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import httpRequest from "../../httpRequest";

const ExpenseHierarchyCard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [hierarchies, setHierarchies] = useState([]);
  const [positions, setPositions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedHierarchyId, setSelectedHierarchyId] = useState(null);
  const [selectedPositionId, setSelectedPositionId] = useState("");
  const [hierarchyPositions, setHierarchyPositions] = useState([]);
  const [selectedHierarchies, setSelectedHierarchies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabIndex, setTabIndex] = useState(0); // 0 for Invoice, 1 for Expense

  useEffect(() => {
    fetchHierarchies();
    fetchPositions();
  }, [tabIndex]); // Re-fetch hierarchies based on the tab

  const fetchHierarchies = async () => {
    const url =
      tabIndex === 0
        ? `${process.env.REACT_APP_BACKEND_URL}/get-invoice-hierarchies`
        : `${process.env.REACT_APP_BACKEND_URL}/get-expense-hierarchies`;

    try {
      const response = await httpRequest.get(url);
      setHierarchies(response.data.hierarchies);
    } catch (error) {
      console.error("Error fetching hierarchies:", error);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await httpRequest.get(
        `${process.env.REACT_APP_BACKEND_URL}/get-positions`
      );
      setPositions(response.data.positions);
    } catch (error) {
      console.error("Error fetching positions:", error);
    }
  };

  const handleDialogOpen = (hierarchy = {}) => {
    if (hierarchy.id) {
      setIsEditing(true);
      setSelectedPositionId(hierarchy.position_id);
      setHierarchyPositions(hierarchy.hierarchy_positions);
      setSelectedHierarchyId(hierarchy.id);
    } else {
      setIsEditing(false);
      setSelectedPositionId("");
      setHierarchyPositions([]);
      setSelectedHierarchyId(null);
    }
    setDialogOpen(true);
  };

  const handleDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    setDialogOpen(false);
    setSelectedHierarchyId(null);
  };

  const handleAddOrUpdateHierarchy = async () => {
    const hierarchyData = {
      position_id: selectedPositionId,
      hierarchy_positions: hierarchyPositions,
    };

    try {
      const url =
        tabIndex === 0
          ? `${process.env.REACT_APP_BACKEND_URL}/update-invoice-hierarchy/${selectedHierarchyId}`
          : `${process.env.REACT_APP_BACKEND_URL}/update-expense-hierarchy/${selectedHierarchyId}`;

      if (isEditing) {
        await httpRequest.put(url, hierarchyData);
      } else {
        const addUrl =
          tabIndex === 0
            ? `${process.env.REACT_APP_BACKEND_URL}/add-invoice-hierarchy`
            : `${process.env.REACT_APP_BACKEND_URL}/add-expense-hierarchy`;
        await httpRequest.post(addUrl, hierarchyData);
      }
      fetchHierarchies();
      handleDialogClose();
      enqueueSnackbar(
        `Hierarchy ${isEditing ? "updated" : "added"} successfully`,
        { variant: "success" }
      );
    } catch (error) {
      console.error("Error adding/updating hierarchy:", error);
      enqueueSnackbar("Error adding/updating hierarchy", { variant: "error" });
    }
  };

  const handleDeleteHierarchies = async () => {
    try {
      const deleteUrl =
        tabIndex === 0
          ? `${process.env.REACT_APP_BACKEND_URL}/delete-invoice-hierarchy/`
          : `${process.env.REACT_APP_BACKEND_URL}/delete-expense-hierarchy/`;

      await Promise.all(
        selectedHierarchies.map((id) =>
          httpRequest.delete(`${deleteUrl}${id}`)
        )
      );
      fetchHierarchies();
      enqueueSnackbar("Selected hierarchy(s) deleted successfully", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error deleting hierarchies:", error);
      enqueueSnackbar("Error deleting hierarchies", { variant: "error" });
    }
  };

  const handleSelectChange = (event) => {
    const value = event.target.value;
    setHierarchyPositions(value);
  };

  const columns = [
    {
      field: "position_name",
      headerName: "Position Name",
      flex: 1,
      valueGetter: (params) => {
        const position = positions.find(
          (pos) => pos.id === params.row.position_id
        );
        return position ? position.position_name : "Unknown";
      },
    },
    {
      field: "hierarchy_positions",
      headerName: "Hierarchy Positions",
      flex: 2,
      valueGetter: (params) => {
        const position = positions.find(
          (pos) => pos.id === params.row.position_id
        );
        const positionName = position ? position.position_name : "Unknown";
  
        // Get hierarchy names only if they exist
        const hierarchyNames = params.row.hierarchy_positions.length > 0
          ? params.row.hierarchy_positions
              .map((positionId) =>
                positions.find((pos) => pos.id === positionId)?.position_name
              )
              .filter(Boolean)
              .join(" \u2192 ") // Use arrow only if there are hierarchy positions
          : ""; // Return empty if no hierarchy positions exist
  
        return hierarchyNames ? `${positionName} \u2192 ${hierarchyNames}` : positionName; // Only show position name if no hierarchies
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <ButtonOutlined onClick={() => handleDialogOpen(params.row)}>
          Edit
        </ButtonOutlined>
      ),
    },
  ];

  const filteredHierarchies = hierarchies.filter((hierarchy) => {
    const position = positions.find((pos) => pos.id === hierarchy.position_id);
    const searchLower = searchTerm.toLowerCase();

    return (
      hierarchy.position_id.toString().includes(searchLower) ||
      (position && position.position_name.toLowerCase().includes(searchLower))
    );
  });

  const dialogTitle = isEditing
    ? "Manage Hierarchy"
    : `Add New ${tabIndex === 0 ? "Invoice" : "Expense"} Hierarchy`;

  return (
    <>
    <Card
          header={
            <CardHeader
              subtitleText={`Manage ${
                tabIndex === 0 ? "Invoice" : "Expense"
              } Hierarchies`}
              titleText={`${tabIndex === 0 ? "Invoice" : "Expense"} Approval Workflow`}
            />
          }
          sx={{ width: "100%", overflow: "hidden" }}
        >
     
        
     <Box sx={{ display: "flex", height: "100%" }}>
     <Tabs
  orientation="vertical"
  value={tabIndex}
  onChange={(e, newValue) => setTabIndex(newValue)}
  sx={{ borderRight: 1, borderColor: "divider", width: 200 }}
>
  <Tab label="Invoice" sx={{ textTransform: 'none', fontFamily: 'SapFiori72', fontSize: '16px' }} />
  <Tab label="Expense" sx={{ textTransform: 'none', fontFamily: 'SapFiori72', fontSize: '16px' }} />
</Tabs>



  <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, padding: "20px" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
      <input
        type="text"
        placeholder="Search Positions"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ flex: 1, padding: "10px", marginRight: "20px" }}
      />

      <ButtonOutlined
        onClick={handleDeleteHierarchies}
        {...(selectedHierarchies.length === 0 ? { disabled: true } : {})}
        style={{ marginRight: 8 }}
      >
        <DeleteIcon />
        Delete Selected
      </ButtonOutlined>
      <ButtonContained onClick={() => handleDialogOpen()}>
        <AddIcon />
        Add Hierarchy
      </ButtonContained>
    </div>

    <DataGrid
  rows={filteredHierarchies}
  columns={columns}
  checkboxSelection
  onSelectionModelChange={(ids) => setSelectedHierarchies(ids)}
  getRowId={(row) => row.id}
  autoHeight
  sx={{
    '& .MuiDataGrid-cell': {
      fontFamily: 'SapFiori72',
    },
    '& .MuiDataGrid-columnHeader': {
      fontFamily: 'SapFiori72',
    },
  }}
/>

  </Box>
</Box>
        {/* Dialog for adding/updating hierarchy */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          slotProps={{
            backdrop: {
              onClick: (e) => {
                e.stopPropagation(); // Prevent the click from propagating
              },
            },
          }}
        >
          <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>
            {dialogTitle}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={selectedPositionId}
                    onChange={(e) => {
                      const newPositionId = e.target.value;

                      setHierarchyPositions((prev) =>
                        prev.filter((posId) => posId !== newPositionId)
                      );

                      setSelectedPositionId(newPositionId);
                    }}
                  >
                    {positions.map((position) => (
                      <MenuItem key={position.id} value={position.id}>
                        {position.position_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Hierarchy Positions</InputLabel>
                  <Select
                    multiple
                    value={hierarchyPositions}
                    onChange={handleSelectChange}
                    renderValue={(selected) => (
                      <div>
                        {selected
                          .map((id) => {
                            const position = positions.find(
                              (pos) => pos.id === id
                            );
                            return position ? position.position_name : "";
                          })
                          .join(" \u2192 ")}
                      </div>
                    )}
                  >
                    {positions.map((position) => (
                      <MenuItem
                        key={position.id}
                        value={position.id}
                        disabled={position.id === selectedPositionId} // Disable if it matches the selected position
                      >
                        {position.position_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ButtonOutlined style={{ marginRight: 5 }} onClick={handleDialogClose}>
              Cancel
            </ButtonOutlined>
            <ButtonContained onClick={handleAddOrUpdateHierarchy}>
              {isEditing ? "Update" : "Add"}
            </ButtonContained>
          </DialogActions>
        </Dialog>
      </Card>
    </>
  );
};

export default ExpenseHierarchyCard;
